import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DashboardDetail.css';

const SCROLL_THRESHOLD = 300; // px from bottom to trigger next page load

const DashboardListView = ({
    filterColumn,
    setFilterColumn,
    columnOptions = [],
    filterValue,
    setFilterValue,
    handleFilter,
    clearFilters,
    isFilterApplied,

    gridRef,
    columnDefs,
    rowData,
    defaultColDef,
    onSelectionChanged,
    loading,

    itemsPerPage,
    currentPage,
    setCurrentPage,
    handleItemsPerPageChange,
    totalCount,

    showFilters = true,

    onBodyScroll,
    isFetchingMore = false,
    hasMoreRows = false,
}) => {
    const isInfiniteScroll = typeof onBodyScroll === 'function';

    const scrollContainerRef = useRef(null);
    const rafPendingRef = useRef(false);

    const hasMoreRowsRef = useRef(hasMoreRows);
    const isFetchingRef = useRef(isFetchingMore);
    const onScrollRef = useRef(onBodyScroll);

    useEffect(() => { hasMoreRowsRef.current = hasMoreRows; }, [hasMoreRows]);
    useEffect(() => { isFetchingRef.current = isFetchingMore; }, [isFetchingMore]);
    useEffect(() => { onScrollRef.current = onBodyScroll; }, [onBodyScroll]);

    const enrichedColumnDefs = useMemo(() => {
        const checkboxColDef = {
            colId: 'dlv-sel-col',          // required — gives ag-Grid a stable id
            headerName: '',
            field: '',                     // empty string avoids the null col ref
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: true,
            width: 48,
            minWidth: 48,
            maxWidth: 48,
            resizable: false,
            sortable: false,
            filter: false,
            suppressMenu: true,            // no hamburger menu on this column
            suppressMovable: true,
            lockPosition: 'left',          // string form — avoids deprecated boolean
            headerClass: 'dlv-checkbox-header',
            cellClass: 'dlv-checkbox-cell',
        };
        return [checkboxColDef, ...(columnDefs || [])];
    }, [columnDefs]);

    // ── Scroll handler ────────────────────────────────────────────────────────
    const handleContainerScroll = useCallback(() => {
        if (!hasMoreRowsRef.current || isFetchingRef.current) return;
        if (rafPendingRef.current) return;

        rafPendingRef.current = true;
        requestAnimationFrame(() => {
            rafPendingRef.current = false;
            const container = scrollContainerRef.current;
            if (!container) return;
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
                onScrollRef.current?.({ target: container });
            }
        });
    }, []);

    // ── Attach / detach listener ──────────────────────────────────────────────
    useEffect(() => {
        if (!isInfiniteScroll) return;
        const container = scrollContainerRef.current;
        if (!container) return;
        container.addEventListener('scroll', handleContainerScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleContainerScroll);
    }, [isInfiniteScroll, handleContainerScroll]);

    // ── Auto-load when no overflow ────────────────────────────────────────────
    useEffect(() => {
        if (!isInfiniteScroll) return;
        if (!hasMoreRows || isFetchingMore || loading) return;
        const timer = setTimeout(() => {
            const container = scrollContainerRef.current;
            if (!container) return;
            if (container.scrollHeight <= container.clientHeight) {
                onScrollRef.current?.({ target: container });
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [isInfiniteScroll, hasMoreRows, isFetchingMore, loading, rowData]);

    // ── Pagination helpers ────────────────────────────────────────────────────
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    const canGoPrev = currentPage > 1;
    const canGoNext = currentPage < totalPages;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="dlv-root">

            {/* Scrollable AG-Grid container */}
            <div ref={scrollContainerRef} className="dlv-grid-container">
                <div className="ag-theme-alpine" style={{ width: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={enrichedColumnDefs}
                        defaultColDef={defaultColDef}
                        onSelectionChanged={onSelectionChanged}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        animateRows
                        domLayout="autoHeight"
                        overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading…</span>'
                        overlayNoRowsTemplate='<span style="padding:20px;color:#888;">No records found</span>'
                        loading={loading}
                        suppressPaginationPanel
                        pagination={false}
                    />
                </div>
            </div>

            {/* Loading more strip */}
            {isInfiniteScroll && isFetchingMore && (
                <div className="dlv-fetch-loader">
                    <span className="dlv-fetch-spinner" />
                    Loading more records…
                </div>
            )}

            {/* End-of-list notice */}
            {isInfiniteScroll && !hasMoreRows && !loading && rowData?.length > 0 && (
                <div className="dlv-end-notice">
                    All {totalCount} records loaded
                </div>
            )}

            {/* Pagination bar (non-infinite mode only) */}
            {!isInfiniteScroll && (
                <div className="dlv-pagination">
                    <div className="dlv-pagination-left">
                        <span>Rows per page:</span>
                        <select
                            className="dlv-select"
                            style={{ minWidth: '70px' }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>

                    <div className="dlv-pagination-right">
                        <button className="dlv-page-btn" onClick={() => setCurrentPage(1)} disabled={!canGoPrev}>«</button>
                        <button className="dlv-page-btn" onClick={() => setCurrentPage((p) => p - 1)} disabled={!canGoPrev}>‹</button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button className="dlv-page-btn" onClick={() => setCurrentPage((p) => p + 1)} disabled={!canGoNext}>›</button>
                        <button className="dlv-page-btn" onClick={() => setCurrentPage(totalPages)} disabled={!canGoNext}>»</button>
                    </div>

                    <span className="dlv-record-total">{totalCount} record(s) total</span>
                </div>
            )}
        </div>
    );
};

export default DashboardListView;